// Shared config reading + validation for the `numeric_grid` runtime card.
//
// A numeric_grid is N number fields on one card: "how much does your
// household spend per month on groceries / delivery / eating out / coffee",
// or height and weight together. Each row is independently configured, so a
// row is exactly a `number` question's input config and is evaluated by the
// very same helper. That reuse is deliberate: Swiss separator handling
// (1'234.50, 12,5) and the decimals/min/max rules must not drift between a
// standalone number question and a row of a grid.
//
// Config lives on the question content as:
//   content.input.required          card-level: every row must be filled
//   content.rows[].id               '^row_[1-9][0-9]*$'
//   content.rows[].label            i18n, the row's own name
//   content.rows[].tooltip_body     i18n, optional per-row help
//   content.rows[].input            { min, max, decimals, unit } per row
//
// Per-row rather than inherited-from-the-card, because height (cm, 0 dp) and
// weight (kg, 1 dp) sit on one card. The editor offers "apply to every row"
// so the common case (four CHF amounts) is still one edit; the *shape* stays
// flat so the runtime never has to merge a card default with a row override.

import { isNumberValueValid } from './numberValidation.js';

/** Card-level: must every row be filled before the engine lets us advance? */
export function gridRequired(item) {
  const input = (item && item.item && item.item.content && item.item.content.input) || {};
  return input.required === true;
}

/**
 * The rows of a grid, normalized. Each carries a `cfg` in exactly the shape
 * evaluateNumber() expects, so callers never touch the raw JSON.
 *
 * `required` is intentionally absent from the row cfg: requiredness is a
 * card-level decision here, and evaluateNumber() would otherwise flag every
 * empty row of an optional card as an error while the user is still typing.
 */
export function gridRows(item) {
  const rows = (item && item.item && item.item.content && item.item.content.rows);
  if (!Array.isArray(rows)) return [];
  return rows.map((r, idx) => {
    const input = (r && r.input) || {};
    return {
      id: (r && r.id != null) ? r.id : `row_${idx + 1}`,
      label: (r && r.label) || null,
      tooltip_body: (r && r.tooltip_body) || null,
      cfg: {
        required: false,
        min: _numOrNull(input.min),
        max: _numOrNull(input.max),
        decimals: Number.isInteger(input.decimals) && input.decimals >= 0 ? input.decimals : 0,
        unit: (input.unit && typeof input.unit === 'object') ? input.unit : null,
      },
    };
  });
}

function _numOrNull(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Engine convenience: is this grid answered?
 *
 * Answered === every row holds a present, in-range, correctly-precise
 * number. Same all-or-nothing rule as matrix, and for the same reason: a
 * half-filled grid should not fill its progress segment. On an optional
 * card that only affects the progress bar, never the ability to advance.
 */
export function isNumericGridAnswered(item, state) {
  const rows = gridRows(item);
  if (rows.length === 0) return false;
  const values = (state && state.values) || {};
  return rows.every(row => isNumberValueValid(values[row.id], row.cfg));
}
